import React, { useState, useEffect, useCallback } from 'react';
import { CalendarDays, Filter, X, Phone, MapPin, Briefcase, BadgeCheck, RefreshCw, Eye, Image as ImageIcon, CheckCircle, XCircle } from 'lucide-react';
import StaffDateGate, { gateScopeParams } from './StaffDateGate';
import useAutoRefresh from '../../lib/useAutoRefresh';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Leave Management — admin view of staff leave records.
 *
 * Data load is gated behind a staff + date-range filter (same pattern as
 * AllVisits) so the page never fetches every leave record across the org on
 * mount. After Load is tapped, the secondary master filters (Geo / Dept /
 * Designation / Status) refine the loaded set.
 */
export default function LeaveManagement() {
  const [gate, setGate] = useState({ geo_fence_id: null, geo_fence_name: null, staff: null, from: '', to: '' });
  const [refine, setRefine] = useState({ geo_fence_id: '', department_id: '', designation_id: '', status: '' });
  const [masters, setMasters] = useState({ geos: [], depts: [], desigs: [] });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [openLeave, setOpenLeave] = useState(null);

  const fetchMasters = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      const [g, d, ds] = await Promise.all([
        fetch(`${API_URL}/api/geofences`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/departments`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API_URL}/api/admin/designations`, { headers }).then(r => r.ok ? r.json() : []),
      ]);
      setMasters({ geos: g, depts: d, desigs: ds });
    } catch {}
  }, []);

  const fetchList = useCallback(async () => {
    const scope = gateScopeParams(gate);
    if (!scope) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const qs = new URLSearchParams(scope);
      Object.entries(refine).forEach(([k, v]) => { if (v) qs.set(k, v); });
      const r = await fetch(`${API_URL}/api/admin/leaves?${qs.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setRows(await r.json());
    } finally {
      setLoading(false);
    }
  }, [gate.geo_fence_id, gate.staff?.id, gate.from, gate.to, refine]);

  useEffect(() => { fetchMasters(); }, [fetchMasters]);

  // When refinement filters change after first load, refetch.
  useEffect(() => { if (hasLoaded) fetchList(); }, [refine, fetchList, hasLoaded]);

  // Silent 30-sec refresh — only after filters are applied.
  useAutoRefresh(async () => { if (hasLoaded) await fetchList(); }, 30_000);

  const applyGate = async () => {
    setHasLoaded(true);
    await fetchList();
  };

  const onAction = async (leaveId, status) => {
    if (!window.confirm(`Mark this leave as ${status}?`)) return;
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_URL}/api/admin/leaves/${leaveId}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, notes: '' }),
    });
    if (r.ok) {
      setOpenLeave(null);
      fetchList();
    } else {
      alert('Failed to update leave');
    }
  };

  return (
    <div data-testid="leave-management">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="text-rose-500" /> Leave Management
          </h1>
          <p className="text-xs text-gray-500">
            {hasLoaded
              ? <>Showing {rows.length} leave record{rows.length === 1 ? '' : 's'} for <strong>{gate.staff?.name}</strong></>
              : 'Pick a staff and date range to load leave records.'}
          </p>
        </div>
        {hasLoaded && (
          <button
            onClick={fetchList}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm"
            data-testid="leave-refresh"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        )}
      </div>

      <div className="mb-4">
        <StaffDateGate value={gate} onChange={setGate} onApply={applyGate} loading={loading} defaultDays={30} />
      </div>

      {!hasLoaded && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center text-blue-700" data-testid="leave-prompt">
          <Eye className="mx-auto mb-2 opacity-60" size={28} />
          <p className="font-medium">Pick a staff and date range, then tap <strong>Load</strong>.</p>
          <p className="text-xs text-blue-600 mt-1">Loads only this staff's leave records in the date window.</p>
        </div>
      )}

      {hasLoaded && (
      <div className="bg-white border rounded-xl p-3 flex flex-wrap gap-2 items-center mb-4" data-testid="leave-filters">
        <div className="flex items-center gap-1 text-gray-500 text-xs"><Filter size={14} /> Refine</div>

        <select
          className="px-3 py-1.5 border rounded text-sm" value={refine.geo_fence_id}
          onChange={(e) => setRefine({ ...refine, geo_fence_id: e.target.value })}
          data-testid="leave-filter-geo"
        >
          <option value="">All Geo Fences</option>
          {masters.geos.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>

        <select
          className="px-3 py-1.5 border rounded text-sm" value={refine.department_id}
          onChange={(e) => setRefine({ ...refine, department_id: e.target.value })}
          data-testid="leave-filter-dept"
        >
          <option value="">All Departments</option>
          {masters.depts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>

        <select
          className="px-3 py-1.5 border rounded text-sm" value={refine.designation_id}
          onChange={(e) => setRefine({ ...refine, designation_id: e.target.value })}
          data-testid="leave-filter-desig"
        >
          <option value="">All Designations</option>
          {masters.desigs.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>

        <select
          className="px-3 py-1.5 border rounded text-sm" value={refine.status}
          onChange={(e) => setRefine({ ...refine, status: e.target.value })}
          data-testid="leave-filter-status"
        >
          <option value="">All status</option>
          <option value="submitted">Recorded</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <button
          onClick={() => setRefine({ geo_fence_id: '', department_id: '', designation_id: '', status: '' })}
          className="text-xs text-gray-600 hover:text-gray-900 underline"
          data-testid="leave-filter-clear"
        >Clear refine</button>
      </div>
      )}

      {hasLoaded && (loading ? (
        <p className="text-center text-gray-500 py-8">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500 border" data-testid="leave-empty">
          No leave records match the current filter.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase">Staff</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase">Geo</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase">Dept · Desig</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase">Days</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase">Reason</th>
                  <th className="px-3 py-2 text-center text-[11px] font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(lv => (
                  <tr
                    key={lv.id}
                    className="hover:bg-rose-50 cursor-pointer"
                    onClick={() => setOpenLeave(lv)}
                    data-testid={`leave-row-${lv.id}`}
                  >
                    <td className="px-3 py-2">
                      <p className="font-semibold text-gray-900">{lv.staff_name}</p>
                      <p className="text-[11px] text-gray-500">{lv.staff_code || lv.staff_id}</p>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{lv.geo_fence_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">
                      <span>{lv.department || '—'}</span>
                      <span className="text-gray-400"> · {lv.designation || '—'}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{lv.from_date} → {lv.to_date}</td>
                    <td className="px-3 py-2 text-right font-bold">{lv.days}</td>
                    <td className="px-3 py-2 text-gray-600 max-w-[220px] truncate">{lv.reason || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        lv.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        lv.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {lv.status === 'submitted' ? 'Recorded' : lv.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenLeave(lv); }}
                        className="p-1 text-rose-600 hover:bg-rose-100 rounded"
                        title="View details"
                        data-testid={`leave-view-${lv.id}`}
                      ><Eye size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {openLeave && (
        <LeavePopup
          leave={openLeave}
          onClose={() => setOpenLeave(null)}
          onAction={onAction}
        />
      )}
    </div>
  );
}

function LeavePopup({ leave, onClose, onAction }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/admin/leaves/${leave.id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (r.ok) setDetail(await r.json());
      } catch {}
    })();
  }, [leave.id]);

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" data-testid="leave-popup">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-900">Leave details</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="leave-popup-close">
            <X size={18} />
          </button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          <div>
            <p className="text-xs text-gray-500 uppercase">Staff</p>
            <p className="text-lg font-bold text-gray-900">
              {leave.staff_name} <span className="text-gray-400 font-mono text-sm ml-2">{leave.staff_code || leave.staff_id}</span>
            </p>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div className="text-gray-700 inline-flex items-center gap-1"><Phone size={13} /> {leave.mobile || '—'}</div>
              <div className="text-gray-700 inline-flex items-center gap-1"><MapPin size={13} /> {leave.geo_fence_name || '—'}</div>
              <div className="text-gray-700 inline-flex items-center gap-1"><Briefcase size={13} /> {leave.department || '—'}</div>
              <div className="text-gray-700 inline-flex items-center gap-1"><BadgeCheck size={13} /> {leave.designation || '—'}</div>
            </div>
          </div>

          <div className="bg-rose-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Period</p>
            <p className="font-semibold text-rose-800">{leave.from_date} → {leave.to_date} · {leave.days} day(s)</p>
            {leave.reason && <p className="text-sm text-gray-700 mt-2"><strong>Reason:</strong> {leave.reason}</p>}
            <p className="text-[11px] text-gray-500 mt-1">Submitted {new Date(leave.created_at).toLocaleString('en-IN')}</p>
          </div>

          {detail?.photo_base64 ? (
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1 inline-flex items-center gap-1">
                <ImageIcon size={12} /> Leave form photo
              </p>
              <img
                src={detail.photo_base64} alt="Leave form"
                className="w-full max-h-[420px] object-contain rounded-lg border"
                data-testid="leave-popup-photo"
              />
            </div>
          ) : (
            <p className="text-xs text-gray-400">No leave-form photo attached.</p>
          )}

          {detail?.approval?.notes && (
            <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">📝 {detail.approval.notes}</div>
          )}
        </div>

        {leave.status === 'submitted' && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t bg-gray-50">
            <button
              onClick={() => onAction(leave.id, 'rejected')}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold flex items-center gap-1"
              data-testid="leave-popup-reject"
            ><XCircle size={14} /> Reject</button>
            <button
              onClick={() => onAction(leave.id, 'approved')}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center gap-1"
              data-testid="leave-popup-approve"
            ><CheckCircle size={14} /> Approve</button>
          </div>
        )}
      </div>
    </div>
  );
}
